// === dotMD Lineal Metre Price Script (BSS discount-aware, product page + collection grid) ===
(function () {
  console.log('✅ lineal-metre-price.js loaded');

  // ----------------------------
  // Utilities
  // ----------------------------
  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      try {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      } catch (e) {}
    }
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  function parseMoneyToCents(text) {
    if (!text) return null;
    const cleaned = String(text).replace(/[^\d.,-]/g, '').trim();
    if (!cleaned) return null;

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    let normalised = cleaned;

    if (lastComma > -1 && lastDot > -1) {
      // Both present: decide decimal separator by whichever occurs last
      if (lastComma > lastDot) {
        // 1.234,56 -> remove dots, comma becomes decimal
        normalised = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        // 1,234.56 -> remove commas
        normalised = cleaned.replace(/,/g, '');
      }
    } else if (lastComma > -1 && lastDot === -1) {
      // Only comma present -> treat as decimal
      normalised = cleaned.replace(',', '.');
    } else {
      // Only dot or none -> remove commas just in case
      normalised = cleaned.replace(/,/g, '');
    }

    const value = Number(normalised);
    if (!Number.isFinite(value)) return null;
    return Math.round(value * 100);
  }

  function isVisible(el) {
    if (!el) return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
    return true;
  }

  function elementHasLineThrough(el) {
    if (!el) return false;

    const style = window.getComputedStyle(el);
    const tdl = style.textDecorationLine || '';
    if (tdl.includes('line-through')) return true;

    // Inline style quick check (BSS uses inline text-decoration often)
    const inline = (el.getAttribute('style') || '').toLowerCase();
    if (inline.includes('line-through')) return true;

    return false;
  }

  function debounceFrame(fn) {
    let raf = null;
    return function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = null;
        fn();
      });
    };
  }

  // ----------------------------
  // Price extraction helpers
  // ----------------------------
  function extractCurrentPriceCentsFromMoneyContainer(moneyEl) {
    // Handles:
    // - Plain "$87.97" inside span.money
    // - Nested spans inside span.money: [struck original, current price]
    if (!moneyEl) return null;

    const innerSpans = Array.from(moneyEl.querySelectorAll('span')).filter(isVisible);
    if (innerSpans.length) {
      const nonStruck = innerSpans.filter(s => !elementHasLineThrough(s) && (s.textContent || '').trim());
      const target = nonStruck.length ? nonStruck[nonStruck.length - 1] : innerSpans[innerSpans.length - 1];
      const cents = parseMoneyToCents(target.textContent);
      if (Number.isFinite(cents)) return cents;
    }

    const cents = parseMoneyToCents(moneyEl.textContent);
    if (Number.isFinite(cents)) return cents;

    return null;
  }

  function getCurrentDisplayedPriceCentsFromPriceCurrent(priceCurrentEl) {
    // Product page: .price__current contains span.money, possibly with nested spans
    if (!priceCurrentEl) return null;

    // Prefer BSS active node if present
    const bssActive = priceCurrentEl.querySelector('[bss-b2b-product-active="true"]');
    const scope = bssActive || priceCurrentEl;

    const money = scope.querySelector('span.money');
    const cents = extractCurrentPriceCentsFromMoneyContainer(money);
    if (Number.isFinite(cents)) return cents;

    // Fallback: any visible non-struck span in scope
    const spans = Array.from(scope.querySelectorAll('span')).filter(isVisible);
    const nonStruck = spans.filter(s => !elementHasLineThrough(s) && (s.textContent || '').trim());
    if (nonStruck.length) {
      const c = parseMoneyToCents(nonStruck[nonStruck.length - 1].textContent);
      if (Number.isFinite(c)) return c;
    }

    return null;
  }

  function getGridDisplayedPriceCents(wrapper) {
    // Grid: prefer BSS active price; else use min "from" price; else use any span.money
    if (!wrapper) return null;

    // 1) BSS active block (discounted)
    const bssActive = wrapper.querySelector('[bss-b2b-product-active="true"]');
    if (bssActive) {
      // In your grid example, the BSS active element contains two spans (struck, current)
      const spans = Array.from(bssActive.querySelectorAll('span')).filter(isVisible);
      const nonStruck = spans.filter(s => !elementHasLineThrough(s) && (s.textContent || '').trim());
      const target = nonStruck.length ? nonStruck[nonStruck.length - 1] : spans[spans.length - 1];
      const cents = parseMoneyToCents(target?.textContent);
      if (Number.isFinite(cents)) return cents;
    }

    // 2) From price (range min)
    const fromMin = wrapper.querySelector('span.money.price__current--min');
    if (fromMin && isVisible(fromMin)) {
      const cents = parseMoneyToCents(fromMin.textContent);
      if (Number.isFinite(cents)) return cents;
    }

    // 3) Regular single price (try to avoid compare-at)
    const current = wrapper.querySelector('.price__current span.money, span.money.price__current--single, span.money.price__current');
    if (current) {
      const cents = parseMoneyToCents(current.textContent);
      if (Number.isFinite(cents)) return cents;
    }

    // 4) Last resort: any money
    const anyMoney = wrapper.querySelector('span.money');
    const cents = parseMoneyToCents(anyMoney?.textContent);
    if (Number.isFinite(cents)) return cents;

    return null;
  }

  // ----------------------------
  // Product page
  // ----------------------------
  function initProductPage() {
    const container = document.getElementById('lineal-metre-price');
    if (!container) return;

    const variantSelect =
      document.querySelector('select[name="id"]') ||
      document.querySelector('form[action*="/cart/add"] select');

    const jsonEl = document.querySelector('[id^="product-json-"]');
    if (!jsonEl || !variantSelect) return;

    let productData = null;
    try {
      productData = JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.warn('lineal-metre: JSON parse failed');
      return;
    }

    if (!productData?.variants?.length) return;

    function getSelectedVariant() {
      const id = parseInt(variantSelect.value, 10);
      return productData.variants.find(v => v.id === id) || null;
    }

    function getLengthFromSelect() {
      const opt = variantSelect.options[variantSelect.selectedIndex];
      const txt = (opt?.textContent || '').trim();

      // Supports "3.6M", "3.6 m", "3.6m", "3.6"
      const m = txt.match(/([\d.]+)\s*m/i) || txt.match(/([\d.]+)/);
      return m ? parseFloat(m[1]) : null;
    }

    function render() {
      const variant = getSelectedVariant();
      const length = getLengthFromSelect();
      if (!variant || !length) {
        container.innerHTML = '';
        return;
      }

      // Prefer displayed price (BSS/discount aware)
      const priceCurrentEl =
        document.querySelector('.price__current[data-price-container]') ||
        document.querySelector('.price__current');

      let priceEachCents = getCurrentDisplayedPriceCentsFromPriceCurrent(priceCurrentEl);

      // Fallback to Shopify variant JSON price
      if (!Number.isFinite(priceEachCents)) priceEachCents = variant.price;

      if (!Number.isFinite(priceEachCents) || priceEachCents <= 0) {
        container.innerHTML = '';
        return;
      }

      const perMetreCents = Math.round(priceEachCents / length);

      container.innerHTML =
        '<div class="per-metre-price" style="font-size:0.95em; margin-top:4px; color:#333;">' +
        formatMoney(perMetreCents) +
        ' / lineal metre</div>';
    }

    const renderDebounced = debounceFrame(render);

    // Initial render
    setTimeout(renderDebounced, 250);

    // On variant change
    variantSelect.addEventListener('change', () => setTimeout(renderDebounced, 50));

    // Shopify editor updates
    document.addEventListener('shopify:section:load', renderDebounced);
    document.addEventListener('shopify:section:select', renderDebounced);

    // BSS / price swaps: observe price area
    const watch =
      document.querySelector('.price__current[data-price-container]') ||
      document.querySelector('.price');

    if (watch) {
      const mo = new MutationObserver(renderDebounced);
      mo.observe(watch, { childList: true, subtree: true, characterData: true });
    }

    console.log('✅ lineal-metre product page initialised');
  }

  // ----------------------------
  // Collection grid
  // ----------------------------
  function initGrid() {
    let wrappers = Array.from(document.querySelectorAll('.product-card__price-wrapper[data-stick-length]'));
    if (!wrappers.length) return;

    function getStickLength(wrapper) {
      const raw = wrapper.getAttribute('data-stick-length') || wrapper.dataset.stickLength;
      const v = raw ? parseFloat(raw) : NaN;
      return Number.isFinite(v) && v > 0 ? v : null;
    }

    function ensureLmEl(wrapper) {
      let el = wrapper.querySelector('.grid-lineal-metre-price');
      if (!el) {
        el = document.createElement('div');
        el.className = 'grid-lineal-metre-price';
        el.style.fontSize = '0.85em';
        el.style.color = '#555';
        el.style.marginTop = '2px';
        wrapper.appendChild(el);
      }
      return el;
    }

    function updateWrapper(wrapper) {
      const length = getStickLength(wrapper);
      const cents = getGridDisplayedPriceCents(wrapper);
      if (!length || !Number.isFinite(cents) || cents <= 0) return;

      const perMetreCents = Math.round(cents / length);
      const lmEl = ensureLmEl(wrapper);
      lmEl.textContent = formatMoney(perMetreCents) + ' / lineal metre';
    }

    const updateAll = debounceFrame(() => {
      wrappers = Array.from(document.querySelectorAll('.product-card__price-wrapper[data-stick-length]'));
      wrappers.forEach(updateWrapper);
    });

    // Initial
    setTimeout(updateAll, 250);

    // Watch each wrapper for BSS swaps / price changes
    wrappers.forEach(wrapper => {
      const mo = new MutationObserver(updateAll);
      mo.observe(wrapper, { childList: true, subtree: true, characterData: true });
    });

    console.log('✅ lineal-metre grid initialised');
  }

  // ----------------------------
  // Boot
  // ----------------------------
  function boot() {
    initProductPage();
    initGrid();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();